import { A } from '@ember/array';
import { Promise } from 'rsvp';
import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { SH_CUSTOM_ERROR_CODES, SH_ERROR_INDEX } from 'sh-www/utils/adapter-errors';
import { task } from 'ember-concurrency';
import { APPLICATION } from "../../../models/application";
import { absoluteStoryhunterUrl } from "../../../utils/url";
import { showModal } from "sh-www/utils/modal-controls";
import { EMBER_ROUTE_NAMES } from "../../../constants/routes";
import {
  getProjectCompletionApplicationIncludes,
  PROJECT_COMPLETION__SUB_VIEWS
} from 'sh-www/constants/routes/project-completion';
import { SH_BILLABLE_LINE_ITEM } from "../../../models/sh-billable-line-item";
import { SH_PAYMENT } from "../../../models/sh-payment";

export default Service.extend({
  /**
   * Register Services
   */
  applicationCommandService: service('commands/application-commands'),
  locationCommandService : service('commands/location-commands'),
  shInvoiceCommandService: service('commands/sh-invoice-commands'),
  shPaymentCommandService: service('commands/sh-payment-commands'),
  applicationStateManager: service('state-managers/application'),
  session: service(),
  serviceCommandService: service('commands/service-commands'),
  state: service('states/routes/project-completion'),
  store: service(),
  companyConnectionCommands: service('commands.company-connection-commands'),
  router: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * Clear and reinitialize the state manager
   */
  initStateManager() {
    this.state.initializeDefaults();
  },

  /**
   * Handle state transitions when the back button is clicked in completion flow
   */
  handleBackClicked() {
    let routeToGoBackTo = EMBER_ROUTE_NAMES.PROJECT__DETAILS;

    switch (this.get('state.subView')) {
      case PROJECT_COMPLETION__SUB_VIEWS.PUBLIC_FEEDBACK:
        routeToGoBackTo = EMBER_ROUTE_NAMES.PROJECT__COMPLETE;
        break;
    }

    this.get('applicationStateManager').setNextRoute(
      routeToGoBackTo,
      null,
      this.get('state.application.id'));
  },

  /**
   * Handle updated state after user cancels the flow.
   */
  handleCancelClicked() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS, this.state.application.id);
  },

  /**
   * @param {String} amount
   */
  handleBaseAmountChange(amount) {
    this.set('state.advanceBaseAmount', amount);
  },

  /**
   * @param {String} text
   */
  handleBaseDescriptionChange(text) {
    this.set('state.advanceBaseDescription', text);
  },

  /**
   * Check to see if the services have been changed. If so, update them.
   */
  handleServicesUpdated: task(function *(newServices) {
    const oldServices = this.get('state.application.services');

    const oldServiceIDs = oldServices.map((service) => service.get('id'));
    const newServiceIDs = newServices.map((service) => service.get('id'));

    // The two sorted arrays should be identical if there are no changes.
    const isChanged = JSON.stringify(oldServiceIDs.sort()) !== JSON.stringify(newServiceIDs.sort());

    let isFailed = false;

    // Update the services if necessary.
    if (isChanged) {
      this.get('state.application').set('services', newServices);

      yield this.get('applicationCommandService')
        .updateApplication(this.get('state.application'), { services: newServiceIDs })
        .then(() => {
          this.get('applicationStateManager').addToast('Services updated');
        })
        .catch(() => {
          this.get('state.application').set('services', oldServices);
          isFailed = true;
        });
    }

    return isFailed
      ? Promise.reject(false)
      : Promise.resolve(true);
  }),

  loadApplication: task(function *(applicationIdOrSlug, opt_additionalIncludes = []) {
    try {
      const response = yield this.applicationCommandService.loadApplication(applicationIdOrSlug, opt_additionalIncludes);
      this.store.pushPayload(response);
      const application = this.store.peekRecord('application', response.data.id);
      return this.setApplication(application);
    } catch(e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error loading this application. Please try again.',
        'loadApplication -- error loading application.',
      );
    }
  }),

  /**
   * @param {DS.Model} newApplication
   * @return {DS.Model}
   */
  setApplication(newApplication) {
    const oldId = this.get('application.id');
    this.set('state.application', newApplication);

    if (!this.get('state.isAdvancePayment') && newApplication.get('id') !== oldId) {
      this.set('state.expedite', newApplication.get('baseShPayment.expedite'));
    }
    return newApplication;
  },

  /**
   * Toggles the Advance payment experience on
   *
   * @param isAdvancePayment
   */
  setIsAdvancePayment(isAdvancePayment) {
    this.set('state.isAdvancePayment', isAdvancePayment);
  },

  /**
   * Set the current sub view which maps to the route.  Using this method because ember current route is a pain
   * in 2.14
   *
   * @param {String} subView      of type PROJECT_COMPLETION__SUB_VIEWS.*
   */
  setSubView(subView) {
    this.set('state.subView', subView);
  },

  setNotesForTeamMessage(text) {
    this.set('state.notesForTeamMessage', text);
  },

  setNotesForGlimmerMessage(text) {
    this.set('state.notesForStoryhunterStaffMessage', text);
  },

  /**
   * Call this function after the application's cost/story codes have been updated.
   */
  storyCodesUpdated() {
    // Validate buyer input
    if (!this.validateBuyerInput()) {
      throw new Error('Missing required fields');
    }

    this.get('applicationStateManager').addToast('Code updated');
  },

  /**
   * Handle updates when the user saves their selection in the edit location modal.
   */
  handleLocationUpdated: task(function *(location) {
    yield this.applicationCommandService
      .updateApplicationLocation(this.state.application, location)
      .then(() => {
        // Set the new location, display the notification, and clear the error flag.
        this.set('state.application.location', location);
        this.applicationStateManager.addToast('Location updated');

        // Validate buyer input
        if (!this.validateBuyerInput()) {
          throw new Error('Missing required fields');
        }
      });
  }),

  /**
   * Handle deletions when the user saves an empty selection in the edit location modal.
   */
  handleLocationDeleted: task(function *() {
    yield this.get('applicationCommandService')
      .deleteApplicationLocation(this.get('state.application'))
      .then(() => {
        // Set the new location and display the notification.
        this.get('state.application').set('location', null);
        this.get('applicationStateManager').addToast('Location removed');
      });
  }),

  /**
   * Submit the private feedback from the buyer on the application.
   */
  submitPrivateFeedbackBuyer: task(function *() {
    this.set('state.isSubmittingPrivateFeedback', true);
    let isFailed = false,
      promises = [];

    if (this.get('state.notesForTeamMessage')) {
      promises.push(this.get('applicationCommandService').addFreelancerNoteFromBuyer(
        this.get('state.application'),
        this.get('state.notesForTeamMessage')
      ).catch(() => {
        this.set('state.isSubmittingPrivateFeedback', false);
        isFailed = true;
      }));
    }

    if (this.get('state.notesForStoryhunterStaffMessage')) {
      promises.push(this.get('applicationCommandService').addPrivateCommentFromBuyer(
        this.get('state.application'),
        this.get('state.notesForStoryhunterStaffMessage')
      ).catch(() => {
        this.set('state.isSubmittingPrivateFeedback', false);
        isFailed = true;
      }));
    }

    if (this.get('state.isAddToNetworkSelected') && !this.get('state.application.seller.inCurrentUsersNetwork')) {
      promises.push(
        this.companyConnectionCommands.updateNetworkConnection(
          this.session.currentUser.company,
          this.state.application.seller,
          null,
          true
        ).catch(() => {
          this.set('state.isSubmittingPrivateFeedback', false);
          isFailed = true;
        })
      );
    }

    yield Promise.all(promises);

    if (isFailed) {
      return Promise.reject(false);
    }

    return Promise.resolve(true);
  }),

  /**
   * Submit the private feedback from the seller form to the db
   */
  submitPrivateFeedbackSeller: task(function *() {
    this.set('state.isSubmittingPrivateFeedback', true);
    let isFailed = false,
      promises = [];

    promises.push(this.get('applicationCommandService').addSellerReview(
      this.get('state.application'),
      this.get('state.reviewStarCountSelected'),
      this.get('state.notesForStoryhunterStaffMessage')
    ).catch(() => {
      this.set('state.isSubmittingPrivateFeedback', false);
      isFailed = true;
    }));

    yield Promise.all(promises);

    if (isFailed) {
      return Promise.reject(false);
    }

    return Promise.resolve(true);
  }),

  /**
   * Submit the public feedback from the buyer on the application.
   */
  submitPublicFeedbackBuyer: task(function *() {
    let isFailed = false;

    yield this.get('applicationCommandService').addReviewBuyer(
      this.get('state.application'),
      this.get('state.reviewStarCountSelected'),
      this.get('state.reviewTextMessage')
    ).catch(() => {
      isFailed = true;
    });

    yield this.get('applicationCommandService').markComplete(this.get('state.application')).catch(() => {
      isFailed = true;
    });

    if (isFailed) {
      return Promise.reject(false);
    }

    yield this.loadApplication.perform(this.state.application.id);

    if (this.get('state.application.status') === APPLICATION.STATUS.COMPLETED) {
      this.get('applicationStateManager').addToast('Completion confirmed');
    } else {
      this.get('applicationStateManager').addToast('Project marked complete');
    }

    return Promise.resolve(true);
  }),

  /**
   * Approve an existing Advance payment
   */
  agreeAdvancePayment: task(function *() {
    // Buyer has to update some other fields
    if (this.get('state.isBuyerViewing')) {
      // Validate buyer input
      if (!this.validateBuyerInput()) {
        throw new Error('Missing required fields');
      }
    }

    const shPayment = this.get('state.approvingAdvanceBillingRecord.shPayment');
    const shPaymentCommandService = this.get('shPaymentCommandService');

    // If user has elected to expedite, submit that first
    if (this.state.expedite && this.state.takeoverExpediteFee) {
      // Mark expedited
      yield shPaymentCommandService.expeditePaymentTakeoverFees(
        shPayment,
        SH_PAYMENT.DEFAULT_INCLUDES(this.state.currentUser)
      );
    } else if (!shPayment.expedite && this.state.expedite) {
      // Mark expedited
      yield shPaymentCommandService.expeditePayment(
        shPayment,
        SH_PAYMENT.DEFAULT_INCLUDES(this.get('state.currentUser'))
      );
    }

    // Submit advance
    yield shPaymentCommandService.get('agreeAdvance').perform(shPayment);
  }),

  /**
   * Create a new Advance payment
   */
  submitAdvancePayment: task(function *() {
    if (this.get('state.isBuyerViewing')) {
      // Validate buyer input
      if (!this.validateBuyerInput()) {
        throw new Error('Missing required fields');
      }
    }

    const baseAmount = (this.get('state.advanceBaseSelected') && this.get('state.advanceBaseAmount'))
      ? this.get('state.advanceBaseAmount')
      : 0;

    try {
      return yield this.get('applicationCommandService')
        .createAdvance({
          application: this.get('state.application'),
          baseAmount: baseAmount,
          baseDescription: this.get('state.advanceBaseDescription'),
          expedite: this.get('state.expedite'),
          advanceProjectLineItemIds: this.get('state.advanceProjectLineItemIds')
        });
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was a problem creating your advance. Please try again or contact support.',
        'submitAdvancePayment -- error creating advance.',
      );
      throw e;
    }
  }),

  /**
   * Toggle the add to network state - i.e. the checkbox
   */
  toggleAddToNetworkChecked() {
    this.set('state.isAddToNetworkSelected', !this.get('state.isAddToNetworkSelected'));
  },

  /**
   * Validate buyer input
   * @return {bool} True if pass, false on failure
   */
  validateBuyerInput() {
    let hasErrors = false;

    // Validate location.
    if (
      !this.get('state.application.location.placeId')
      || this.get('state.application.location.locationType') === 'country'
    ) {
      this.set('state.isLocationErrorShowing', true);
      hasErrors = true;
    } else {
      this.set('state.isLocationErrorShowing', false);
    }

    // Validate codes.
    if (
      this.get('state.application.buyer.company.doesRequireCostCodes')
      && !this.get('state.application.costCode')
    ) {
      this.set('state.isCostCodeErrorShowing', true);
      hasErrors = true;
    } else {
      this.set('state.isCostCodeErrorShowing', false);
    }

    return !hasErrors;
  },

  /**
   * Update the project information form based on the user input, and save down to the db.
   */
  updateProjectInformationFormBuyer: task(function *() {
    // Validate buyer input
    if (!this.validateBuyerInput()) {
      throw new Error('Missing required fields');
    }

    this.set('state.isSubmittingProjectInformationForm', true);

    try {
      // Deal with changes to expediting state
      const baseShPayment = this.get('state.application.baseShPayment');
      if (this.state.expedite && this.state.takeoverExpediteFee) {
        // Takeover fees on expedited project
        yield this.applicationCommandService.expeditePayoutTakeoverFees(
          this.state.application,
          getProjectCompletionApplicationIncludes(this.currentUser),
        );
      } else if (!baseShPayment.expedite && this.state.expedite) {
        // Mark expedited
        yield this.get('applicationCommandService').expeditePayout(
          this.get('state.application'),
          getProjectCompletionApplicationIncludes(this.currentUser),
        );
      } else if (
        baseShPayment.get('expedite')
        && baseShPayment.get('expediteRequestor.id') === this.get('currentUser.id')
        && !this.get('state.expedite')
      ) {
        // Un-mark expedited
        yield this.get('applicationCommandService').withdrawExpeditedPayout(
          this.get('state.application'),
          getProjectCompletionApplicationIncludes(this.currentUser),
        );
      }
    } catch(e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error changing the expedite. Please refresh and try again.',
        'updateProjectInformationFormBuyer -- error changing the expedite on application: ' + this.state.application.id,
      );
    } finally {
      this.set('state.isSubmittingProjectInformationForm', false);
    }
  }),

  /**
   * Update the project information form based on the user input from seller and save down to the db.
   */
  updateProjectInformationFormSeller: task(function *() {
    let isFailed = false;
    this.set('state.isSubmittingProjectInformationForm', true);

    // Deal with changes to expediting state
    const baseShPayment = this.get('state.application.baseShPayment');
    if (!baseShPayment.get('expedite') && this.get('state.expedite')) {
      // Mark expedited
      yield this.get('applicationCommandService').expeditePayout(
        this.get('state.application'),
        getProjectCompletionApplicationIncludes(this.currentUser),
      );
    } else if (
      baseShPayment.get('expedite')
      && baseShPayment.get('expediteRequestor.id') === this.get('currentUser.id')
      && !this.get('state.expedite')
    ) {
      // Un-mark expedited
      yield this.get('applicationCommandService').withdrawExpeditedPayout(
        this.get('state.application'),
        getProjectCompletionApplicationIncludes(this.currentUser),
      );
    }

    let errorFiledDueToNowPaymentAccount = null;
    yield this.get('applicationCommandService').markComplete(this.get('state.application')).catch(e => {
      let errors = e.errors || null;
      if (errors && errors[SH_ERROR_INDEX.CUSTOM_ERROR_CODE] === SH_CUSTOM_ERROR_CODES.ERROR_CODE_PAYOUT_ACCOUNT_REQUIRED) {
        errorFiledDueToNowPaymentAccount = errors[SH_ERROR_INDEX.CUSTOM_ERROR_BODY];
      }

      this.set('state.isSubmittingProjectInformationForm', false);
      isFailed = true;
    });

    if (errorFiledDueToNowPaymentAccount) {
      let modalModel = {
        titleText: 'Payment not set up',
        bodyText: errorFiledDueToNowPaymentAccount,
        actionButtonText: 'Set up payment',
        cancelButtonText: 'Cancel',
        isShowCancelButton: true,
        confirmAction: () => {
          window.location.assign(absoluteStoryhunterUrl('/settings/payout'));
        }
      };

      showModal(this, 'confirmation', modalModel);
      return Promise.reject(false);
    }

    yield this.loadApplication.perform(this.state.application.id)
      .catch(() => {
        this.set('state.isSubmittingProjectInformationForm', false);
        isFailed = true;
      }
    );

    if (isFailed) {
      return Promise.reject(false);
    }

    if (this.get('state.application.status') === APPLICATION.STATUS.COMPLETED) {
      this.get('applicationStateManager').addToast('Completion confirmed');
    } else {
      this.get('applicationStateManager').addToast('Project marked complete');
    }

    this.set('state.isSubmittingProjectInformationForm', false);
    return Promise.resolve(true);
  }),

  /**
   * @param {Number} starCount      The actual count and not the star index (i.e. 1 based)
   */
  updateReviewStarCountSelected(starCount) {
    this.set('state.reviewStarCountSelected', starCount);
  },

  /**
   * Sets the test BillingRecord and updates related properties
   *
   * @param testBillingRecord
   */
  setTestBillingRecord(testBillingRecord) {
    this.set('state.testBillingRecord', testBillingRecord);

    if (testBillingRecord) {
      // Existing billable

      // Calculate selected expense project line items
      const advanceProjectLineItemIds = A();

      testBillingRecord.get('shBillableLineItems').forEach((billableLineItem) => {
        if (billableLineItem.get('type') === SH_BILLABLE_LINE_ITEM.TYPES.EXPENSE) {
          advanceProjectLineItemIds.pushObject(billableLineItem.get('derivedFromProjectLineItem.id'));
        }
      });

      this.set('state.advanceProjectLineItemIds', advanceProjectLineItemIds);

      // Calculate base amounts
      const baseLineItem = testBillingRecord.get('shBillableLineItems').find((shBillableLineItem) => {
        return shBillableLineItem.get('type') === SH_BILLABLE_LINE_ITEM.TYPES.BASE;
      });

      if (baseLineItem) {
        this.set('state.advanceBaseAmount', baseLineItem.get('amount'));
        this.set('state.advanceBaseSelected', true);
        this.set('state.advanceBaseDescription', baseLineItem.get('description'));
      }

      this.set('state.expedite', testBillingRecord.get('expedite'));
    }
  },

  /** Fetches the test billing record from the server. */
  updateTestBillingRecord: task(function *() {
    const application = this.get('state.application');

    if (!application) {
      return;
    }

    const baseAmount = (this.get('state.advanceBaseSelected') && this.get('state.advanceBaseAmount'))
      ? this.get('state.advanceBaseAmount')
      : 0;
    const baseDescription = this.get('state.advanceBaseDescription');

    // If this is an advance, validate it
    if (this.get('state.isAdvancePayment')) {
      if (
        (!baseAmount || Number.parseFloat(baseAmount) < 0.01)
        && this.get('state.advanceProjectLineItemIds.length') === 0
      ) {
        // Need a base amount or an expense at minimum
        this.set('state.testBillingRecord', undefined);
        return;
      }

      if (baseAmount && Number.parseFloat(baseAmount) > 0.0 && !baseDescription) {
        // Need a description if we have a base amount
        this.set('state.testBillingRecord', undefined);
        return;
      }
    }

    try {
      const response = yield this.get('applicationCommandService').getTestBillingRecord({
        application: application,
        expedite: this.get('state.expedite'),
        takeoverExpediteFee: this.state.takeoverExpediteFee,
        userType: this.get('state.isBuyerViewing') ? 'buyer' : 'seller',
        isAdvance: this.get('state.isAdvancePayment'),
        advanceBaseAmount: baseAmount,
        advanceProjectLineItemIds: this.get('state.advanceProjectLineItemIds'),
        advanceBaseDescription: baseDescription
      });

      const store = this.get('store');
      store.pushPayload(response);
      const testBillingRecord = store.peekRecord(
        this.get('state.isBuyerViewing')  ? 'shInvoice' : 'shPayment',
        response.data.id
      );

      this.setTestBillingRecord(testBillingRecord);
    } catch (e) {
      this.setTestBillingRecord(undefined);
      throw e;
    }
  }).restartable(),
});
